import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Bar, BarChart, CartesianGrid, Legend, XAxis, YAxis } from "recharts";
import { Money, Percent, Receipt } from "@phosphor-icons/react";
import type { DateRangeValue } from "@/components/admin/DateRangePicker";
import {
  ChartContainer,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { throwIfNotOk } from "@/lib/api-error";
import type { RevenueReportResponse } from "./types";
import { ErrorCard, km, LoadingState, Metric, shortDate } from "./report-shared";

const chartConfig = {
  revenue: {
    label: "Ukupni prihod",
    color: "var(--chart-1)",
  },
  platformFee: {
    label: "Provizija (bez PDV-a)",
    color: "var(--chart-2)",
  },
} satisfies ChartConfig;

const SOURCES: ReadonlyArray<readonly [keyof RevenueReportResponse["bySource"], string]> = [
  ["sessions", "Seanse"],
  ["groupTherapies", "Grupne terapije"],
  ["subscriptions", "Pretplate"],
  ["companies", "Firme"],
];

export function RevenueReportBody({ dateRange }: { dateRange: DateRangeValue }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "reports", "revenue", dateRange.from, dateRange.to],
    queryFn: async () => {
      const params = new URLSearchParams({ from: dateRange.from, to: dateRange.to });
      const res = await fetch(`/api/admin/reports/revenue?${params}`);
      await throwIfNotOk(res, "Greška pri učitavanju izvještaja o prihodima");
      return res.json() as Promise<RevenueReportResponse>;
    },
  });

  const platformShare = useMemo(() => {
    if (!data?.totalRevenue) return 0;
    return Math.round((data.platformFee / data.totalRevenue) * 1000) / 10;
  }, [data]);

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorCard message={(error as Error).message} />;
  if (!data) return null;

  return (
    <>
      <div className="grid gap-3 md:grid-cols-3">
        <Metric
          tone="primary"
          icon={Money}
          label="Ukupno prihodovano"
          value={km(data.totalRevenue)}
          detail={`${data.completedPayments} uplata, sa PDV-om, bez povrata`}
        />
        <Metric
          icon={Percent}
          label="Provizija platforme"
          value={km(data.platformFee)}
          detail={`Bez PDV-a, ${platformShare.toFixed(1)}% prihoda`}
        />
        <Metric
          icon={Receipt}
          label="Terapeutima"
          value={km(data.therapistShare)}
          detail="Obaveza prema terapeutima iz istih uplata"
        />
      </div>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-2 rounded-xl border border-border/60 bg-card px-4 py-3 text-sm md:grid-cols-4">
        {SOURCES.map(([key, label]) => (
          <div key={key} className="flex items-baseline justify-between gap-3">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="font-medium tabular-nums text-foreground">{km(data.bySource[key])}</dd>
          </div>
        ))}
      </dl>

      <ChartContainer config={chartConfig} className="h-[320px] w-full">
        <BarChart accessibilityLayer data={data.series} margin={{ left: 4, right: 8, top: 8 }}>
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="date"
            tickLine={false}
            axisLine={false}
            tickMargin={10}
            minTickGap={24}
            tickFormatter={shortDate}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            width={64}
            tickFormatter={(value) => `${Number(value).toLocaleString("bs-BA")} KM`}
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                indicator="dot"
                labelFormatter={(value) => shortDate(String(value))}
                formatter={(value, name) => (
                  <div className="flex min-w-36 items-center justify-between gap-4">
                    <span className="text-muted-foreground">
                      {chartConfig[String(name) as keyof typeof chartConfig]?.label ?? name}
                    </span>
                    <span className="font-mono font-medium tabular-nums text-foreground">
                      {km(Number(value))}
                    </span>
                  </div>
                )}
              />
            }
          />
          <Legend content={<ChartLegendContent />} />
          <Bar dataKey="revenue" fill="var(--color-revenue)" radius={[4, 4, 0, 0]} />
          <Bar dataKey="platformFee" fill="var(--color-platformFee)" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ChartContainer>
    </>
  );
}
